import { useEffect, useMemo, useState } from "react";
import { usePageState } from "@/contexts/NavigationContext";
import { useNavigate } from "react-router-dom";
import Layout from "@/components/Layout";
import CalendarSalesHistory from "@/pages/_calendar/CalendarSalesHistory";
import CalendarOpportunities from "@/pages/_calendar/CalendarOpportunities";
import ClientPurchaseMap from "@/pages/_calendar/ClientPurchaseMap";
import api from "@/lib/api";
import { fmtL, fmtDate, apiErr } from "@/lib/format";
import { toast } from "sonner";
import {
  CalendarIcon, BellRingingIcon, CaretLeftIcon, CaretRightIcon,
  WarningIcon, ClockIcon, ReceiptIcon, UserCircleIcon, HandCoinsIcon,
  ChartLineUpIcon, ArrowRightIcon, FlagIcon, PlusIcon, BankIcon, AirplaneTiltIcon,
} from "@phosphor-icons/react";
import { useModalForm } from "@/lib/modalForm";
import { useAuth } from "@/contexts/AuthContext";
import BotonCerrar from "@/components/BotonCerrar";

const MESES = ["Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio", "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"];
const DIAS = ["Lun", "Mar", "Mié", "Jue", "Vie", "Sáb", "Dom"];

const ymd = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const startOfWeek = (d) => { const x = new Date(d.getFullYear(), d.getMonth(), d.getDate()); x.setDate(x.getDate() - ((x.getDay() + 6) % 7)); return x; };
const addDays = (d, n) => new Date(d.getFullYear(), d.getMonth(), d.getDate() + n);
const startOfMonth = (y, m) => new Date(y, m, 1);
const endOfMonth = (y, m) => new Date(y, m + 1, 0);

function buildGrid(year, month) {
  const first = startOfMonth(year, month);
  const last = endOfMonth(year, month);
  // Empieza en lunes (Lun=0, Dom=6)
  const startWeekday = (first.getDay() + 6) % 7;
  const days = [];
  for (let i = 0; i < startWeekday; i++) {
    const d = new Date(year, month, -startWeekday + i + 1);
    days.push({ date: d, inMonth: false });
  }
  for (let day = 1; day <= last.getDate(); day++) {
    days.push({ date: new Date(year, month, day), inMonth: true });
  }
  while (days.length % 7 !== 0) {
    const last_d = days[days.length - 1].date;
    days.push({ date: new Date(last_d.getFullYear(), last_d.getMonth(), last_d.getDate() + 1), inMonth: false });
  }
  return days;
}

export default function CalendarAlerts() {
  const navigate = useNavigate();
  const today = new Date();
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth());
  const [selected, setSelected] = useState(ymd(today));
  const [events, setEvents] = useState({});
  const [alerts, setAlerts] = useState(null);
  const [rtcToday, setRtcToday] = useState(null);
  const [dayDetail, setDayDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filterGuardado, setFilter] = usePageState("filter", {});
  // RTC (alcance regional): su calendario es comercial; sin pagos a proveedores, préstamos ni ausencias de RRHH
  const { user } = useAuth() || {};
  const esRtc = Boolean(user?.effective?.alcance_regional);
  const filter = { vencidas: true, por_vencer: true, cobros: true, promesas: true, activacion: true, cxp: true, prestamos: true,
    ausencias: false, actividades: true, oportunidades: true, ...filterGuardado,
    ...(esRtc ? { cxp: false, prestamos: false, ausencias: false } : {}) };
  // Filtros comerciales (aplican a OPORTUNIDADES SUGERIDAS y a los eventos con cliente/región)
  const [com, setCom] = usePageState("filtro-comercial", { region: "", cliente: "", producto: "" });
  const [opsCtl, setOpsCtl] = usePageState("op-controles", { estado: "activas", prioridad: "", incluir_atendidas: false });
  const [opsData, setOpsData] = useState(null);
  const [opsLoading, setOpsLoading] = useState(false);
  const [opciones, setOpciones] = useState(null);
  useEffect(() => { api.get("/reports/historial-ventas/opciones").then((r) => setOpciones(r.data)).catch(() => {}); }, []);
  const loadOps = async () => {
    if (!filter.oportunidades) { setOpsData(null); return; }
    setOpsLoading(true);
    try {
      const params = { anio: year, mes: month + 1, ...opsCtl, ...Object.fromEntries(Object.entries(com).filter(([, v]) => v)) };
      const { data } = await api.get("/calendar/oportunidades", { params });
      setOpsData(data);
    } catch (e) { toast.error(apiErr(e)); }
    finally { setOpsLoading(false); }
  };
  useEffect(() => { loadOps(); /* eslint-disable-next-line */ }, [year, month, JSON.stringify(com), JSON.stringify(opsCtl), filter.oportunidades]);
  const pasaCom = (ev) => (!com.cliente || !ev.cliente_id || ev.cliente_id === com.cliente) && (!com.region || !ev.region || ev.region === com.region);
  // mes | semana | agenda | historial — en el móvil se abre como agenda (lista por día)
  const [view, setView] = useState(() => (window.matchMedia && window.matchMedia("(max-width: 767px)").matches ? "agenda" : "mes"));
  const [weekStart, setWeekStart] = useState(startOfWeek(today));
  const [promesaModal, setPromesaModal] = useState(null);       // {} = nueva
  const [mapaCliente, setMapaCliente] = useState(null);         // activación: mapa de compras / qué ofrecer

  const loadMonth = async () => {
    setLoading(true);
    try {
      const desde = view === "semana" ? ymd(weekStart) : ymd(startOfMonth(year, month));
      const hasta = view === "semana" ? ymd(addDays(weekStart, 6)) : ymd(endOfMonth(year, month));
      const tipos = Object.entries(filter).filter(([_, v]) => v).map(([k]) => k).join(",");
      const [ev, al, rtc] = await Promise.all([
        api.get(`/calendar/events?desde=${desde}&hasta=${hasta}&tipos=${tipos}`),
        api.get("/calendar/alerts"),
        api.get("/calendar/rtc-today"),
      ]);
      setEvents(ev.data.eventos || {});
      setAlerts(al.data);
      setRtcToday(rtc.data);
    } catch (e) { toast.error(apiErr(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { loadMonth(); /* eslint-disable-next-line */ }, [year, month, JSON.stringify(filter), view, weekStart]);

  const loadDay = async (ymdStr) => {
    setSelected(ymdStr);
    try {
      const { data } = await api.get(`/calendar/day/${ymdStr}`);
      setDayDetail(data);
    } catch (e) { toast.error(apiErr(e)); }
  };
  useEffect(() => { loadDay(selected); /* eslint-disable-next-line */ }, []);

  const grid = useMemo(() => buildGrid(year, month), [year, month]);
  const delDia = (opsData?.items || []).filter((o) => o.fecha_sugerida === selected);
  const prev = () => {
    if (view === "semana") { setWeekStart((w) => addDays(w, -7)); return; }
    if (month === 0) { setMonth(11); setYear(y => y - 1); } else setMonth(m => m - 1);
  };
  const next = () => {
    if (view === "semana") { setWeekStart((w) => addDays(w, 7)); return; }
    if (month === 11) { setMonth(0); setYear(y => y + 1); } else setMonth(m => m + 1);
  };
  const goToday = () => { setYear(today.getFullYear()); setMonth(today.getMonth()); setWeekStart(startOfWeek(today)); loadDay(ymd(today)); };
  const refreshAll = () => { loadMonth(); loadDay(selected); loadOps(); };
  const setPromesaEstado = async (ev, estado) => {
    try {
      await api.post(`/calendar/collection-schedules/${ev.schedule_id}/estado`, { estado });
      toast.success("Promesa actualizada");
      refreshAll();
    } catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <Layout
      title="Calendario y Alertas"
      subtitle="Vencimientos, cobros, promesas de pago, recordatorios y gestión diaria del RTC"
      actions={(
        <div className="flex flex-wrap items-center gap-2">
          <button onClick={() => navigate("/ventas/promesas/nueva")} data-testid="cal-crear-promesa"
            className="bg-agro-green hover:bg-agro-green-hover text-black font-medium rounded-md px-4 py-2 text-sm flex items-center gap-2">
            <PlusIcon size={16} weight="bold" /> Crear promesa de pago
          </button>
          <button onClick={() => setPromesaModal({})} data-testid="new-promesa-btn"
            className="border border-border hover:border-agro-green/50 rounded-md px-4 py-2 text-sm flex items-center gap-2">
            <PlusIcon size={16} /> Programar cobro o visita
          </button>
        </div>
      )}
    >
      {/* Alertas en vivo */}
      <AlertCards alerts={alerts} loading={loading} onNavigate={navigate} />
      <CobranzaStrip alerts={alerts} onPickDay={(d) => loadDay(d)} />

      {/* FILTROS COMERCIALES Y LEYENDA */}
      <div className="mt-6 bg-card border border-border/60 rounded-lg p-3 flex flex-wrap items-end gap-3" data-testid="cal-filtros-comerciales">
        <Sel l="REGIÓN" v={com.region} on={(v) => setCom({ ...com, region: v })} testid="cal-f-region"
          ops={(opciones?.regiones || []).map((r) => [r, r])} />
        <Sel l="CLIENTE" v={com.cliente} on={(v) => setCom({ ...com, cliente: v })} testid="cal-f-cliente"
          ops={(opciones?.clientes || []).map((c) => [c.id, c.nombre])} />
        <Sel l="PRODUCTO" v={com.producto} on={(v) => setCom({ ...com, producto: v })} testid="cal-f-producto"
          ops={(opciones?.productos || []).map((p) => [p.id, p.nombre])} />
        {(com.region || com.cliente || com.producto) && (
          <button onClick={() => setCom({ region: "", cliente: "", producto: "" })} className="text-[11px] font-semibold text-text-secondary border border-border rounded-md px-2.5 py-2"
            data-testid="cal-f-limpiar">LIMPIAR FILTROS</button>
        )}
        <div className="ml-auto flex flex-wrap gap-3 text-[10px] tracking-wider" data-testid="cal-leyenda">
          <span className="flex items-center gap-1.5"><i className="w-2.5 h-2.5 rounded-sm bg-teal-400 inline-block" /> ACTIVIDADES PROGRAMADAS</span>
          <span className="flex items-center gap-1.5"><i className="w-2.5 h-2.5 rounded-sm bg-fuchsia-400 inline-block" /> OPORTUNIDADES SUGERIDAS</span>
          <span className="flex items-center gap-1.5"><i className="w-2.5 h-2.5 rounded-sm bg-blue-400 inline-block" /> HISTORIAL DE VENTAS (VISTA)</span>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 mt-4">
        {/* Calendario */}
        <div className="lg:col-span-2 bg-card border border-border/60 rounded-lg overflow-hidden">
          <div className="p-3 sm:p-4 border-b border-border/60 flex flex-wrap items-center justify-between gap-3">
            <div className="flex flex-wrap items-center gap-2 sm:gap-3">
              <button aria-label="Anterior" onClick={prev} data-testid="cal-prev" className="min-h-[40px] min-w-[40px] flex items-center justify-center rounded-md border border-border hover:border-agro-green text-text-secondary hover:text-agro-green"><CaretLeftIcon size={14} /></button>
              <h3 className="font-heading text-base sm:text-lg font-semibold sm:min-w-[160px] text-center">
                {view === "semana" ? `Semana ${fmtDate(ymd(weekStart))} – ${fmtDate(ymd(addDays(weekStart, 6)))}` : `${view === "historial" ? MESES[month].toUpperCase() : MESES[month]} ${year}`}
              </h3>
              <button aria-label="Siguiente" onClick={next} data-testid="cal-next" className="min-h-[40px] min-w-[40px] flex items-center justify-center rounded-md border border-border hover:border-agro-green text-text-secondary hover:text-agro-green"><CaretRightIcon size={14} /></button>
              <button onClick={goToday} data-testid="cal-today" className="ml-2 text-[10px] uppercase tracking-wider text-agro-green hover:underline">Hoy</button>
              <div className="ml-2 flex border border-border rounded-md overflow-hidden text-[10px] uppercase tracking-wider">
                {[["agenda", "Agenda"], ["mes", "Mes"], ["semana", "Semana"], ["historial", "HISTORIAL DE VENTAS"]].map(([k, l]) => (
                  <button key={k} onClick={() => setView(k)} data-testid={`cal-view-${k}`} aria-pressed={view === k}
                    className={`px-2.5 min-h-[36px] ${view === k ? "bg-agro-green/15 text-agro-green" : "text-text-muted hover:text-text-primary"}`}>{l}</button>
                ))}
              </div>
            </div>
            {view !== "historial" && <FilterChips filter={filter} setFilter={setFilter} ocultar={esRtc ? ["cxp", "prestamos", "ausencias"] : []} />}
          </div>

          {view === "historial" ? (
            <CalendarSalesHistory year={year} month={month} />
          ) : view === "semana" ? (
            <WeekView weekStart={weekStart} events={events} selected={selected} today={ymd(today)} onPick={loadDay} />
          ) : view === "agenda" ? (
            <AgendaView events={events} pasa={pasaCom} today={ymd(today)} loading={loading}
              onOpen={(ev) => ev.link && ev.link !== "/calendario" && navigate(ev.link)} onPromesa={setPromesaEstado}
              onMapa={(e) => setMapaCliente(e.cliente_id)} />
          ) : (<>
          {/* Cabecera días */}
          <div className="grid grid-cols-7 gap-px bg-border/40">
            {DIAS.map(d => (
              <div key={d} className="bg-sidebar p-2 text-center text-[10px] uppercase tracking-wider text-text-muted">{d}</div>
            ))}
          </div>

          {/* Celdas */}
          <div className="grid grid-cols-7 gap-px bg-border/40">
            {grid.map((cell, i) => {
              const k = ymd(cell.date);
              const dayEvs = (events[k] || []).filter(pasaCom);
              const isSelected = k === selected;
              const isToday = k === ymd(today);
              const counts = {
                vencida: dayEvs.filter(e => e.tipo === "vencida").length,
                por_vencer: dayEvs.filter(e => e.tipo === "por_vencer").length,
                cobro: dayEvs.filter(e => e.tipo.startsWith("cobro_")).length,
                activacion: dayEvs.filter(e => e.tipo === "activacion").length,
                promesa: dayEvs.filter(e => e.tipo.startsWith("promesa")).length,
                actividad: dayEvs.filter(e => e.actividad).length,
                oportunidad: filter.oportunidades ? (opsData?.por_dia?.[k] || 0) : 0,
                otros: dayEvs.filter(e => ["prestamo", "ausencia", "cxp"].includes(e.tipo)).length,
              };
              return (
                <button
                  key={i}
                  onClick={() => loadDay(k)}
                  data-testid={`cal-day-${k}`}
                  className={`min-h-[78px] p-2 text-left transition relative
                    ${cell.inMonth ? "bg-card hover:bg-white/[0.03]" : "bg-card/50 text-text-muted opacity-50"}
                    ${isSelected ? "ring-2 ring-agro-green ring-inset" : ""}
                  `}
                >
                  <div className={`text-xs font-medium ${isToday ? "text-agro-green font-semibold" : ""}`}>
                    {cell.date.getDate()}
                    {isToday && <span className="ml-1 text-[8px] uppercase tracking-wider bg-agro-green/10 text-agro-green border border-agro-green/30 rounded px-1">hoy</span>}
                  </div>
                  <div className="mt-1 space-y-0.5">
                    {counts.vencida > 0 && <Pill color="red" n={counts.vencida} l="venc." />}
                    {counts.por_vencer > 0 && <Pill color="yellow" n={counts.por_vencer} l="vence" />}
                    {counts.cobro > 0 && <Pill color="green" n={counts.cobro} l="cobro" />}
                    {counts.promesa > 0 && <Pill color="orange" n={counts.promesa} l="prom." />}
                    {counts.activacion > 0 && <Pill color="blue" n={counts.activacion} l="cli." />}
                    {counts.otros > 0 && <Pill color="purple" n={counts.otros} l="otros" />}
                    {counts.actividad > 0 && <Pill color="teal" n={counts.actividad} l="act." />}
                    {counts.oportunidad > 0 && <Pill color="fuchsia" n={counts.oportunidad} l="oport." />}
                  </div>
                </button>
              );
            })}
          </div>
          </>)}
        </div>

        {/* Panel del día seleccionado */}
        <div className="bg-card border border-border/60 rounded-lg overflow-hidden">
          <div className="p-4 border-b border-border/60">
            <div className="text-[10px] uppercase tracking-wider text-text-muted">Detalle del día</div>
            <h3 className="font-heading text-lg font-semibold mt-0.5">
              {dayDetail ? fmtDate(dayDetail.fecha) : "Selecciona un día"}
            </h3>
            {dayDetail && (
              <div className="text-xs text-text-secondary mt-1 flex items-center gap-3">
                <span>{dayDetail.total_eventos} eventos</span>
                {dayDetail.monto_en_juego > 0 && <span className="text-agro-yellow font-medium">{fmtL(dayDetail.monto_en_juego)} en juego</span>}
              </div>
            )}
          </div>
          <div className="max-h-[520px] overflow-y-auto" data-testid="day-detail">
            {!dayDetail || dayDetail.items.length === 0 ? (
              <div className="p-10 text-center text-sm text-text-muted">
                <CalendarIcon size={32} className="mx-auto mb-2 opacity-40" />
                Nada agendado para este día
              </div>
            ) : dayDetail.items.filter(pasaCom).map((ev, i) => (
              <EventRow key={i} ev={ev} onClick={() => ev.link && ev.link !== "/calendario" && navigate(ev.link)} onPromesa={setPromesaEstado}
                onMapa={(e) => setMapaCliente(e.cliente_id)} />
            ))}
            {filter.oportunidades && delDia.length > 0 && (
              <div className="border-t border-fuchsia-500/30" data-testid="day-oportunidades">
                <div className="px-4 pt-3 pb-1 text-[10px] tracking-wider text-fuchsia-300">OPORTUNIDADES SUGERIDAS PARA CONTACTAR ESTE DÍA</div>
                {delDia.slice(0, 12).map((o) => (
                  <div key={o.clave} className="px-4 py-2 border-b border-border/30 text-xs">
                    <div className="font-medium">{o.cliente} <span className="text-text-muted">· {o.region}</span></div>
                    <div className="text-text-secondary">{o.producto} · {o.cantidad_texto} · <span className="text-fuchsia-300">{o.accion}</span></div>
                  </div>
                ))}
                {delDia.length > 12 && <div className="px-4 py-2 text-[11px] text-text-muted">VER EL RESTO EN OPORTUNIDADES DEL MES</div>}
              </div>
            )}
          </div>
        </div>
      </div>

      {filter.oportunidades && (
        <div className="mt-6">
          <CalendarOpportunities data={opsData} loading={opsLoading} controles={opsCtl} setControles={setOpsCtl} onChanged={refreshAll} />
        </div>
      )}

      {/* Bloque del RTC: a gestionar hoy */}
      <RTCTodayBlock data={rtcToday} onNavigate={navigate} />

      {mapaCliente && <ClientPurchaseMap clienteId={mapaCliente} onClose={() => setMapaCliente(null)} />}

      {promesaModal && (
        <PromesaModal initial={promesaModal} defaultDate={selected} onClose={() => setPromesaModal(null)}
          onSaved={() => { setPromesaModal(null); refreshAll(); }} />
      )}
    </Layout>
  );
}

// ============== ALERTAS (4 tarjetas) ==============
function AlertCards({ alerts, loading, onNavigate }) {
  if (loading && !alerts) {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        {[1,2,3,4].map(i => <div key={i} className="h-28 bg-card border border-border/40 rounded-lg animate-pulse" />)}
      </div>
    );
  }
  if (!alerts) return null;
  const hasCxp = !!alerts.cxp_por_pagar;
  return (
    <div className={`grid grid-cols-1 sm:grid-cols-2 ${hasCxp ? "lg:grid-cols-5" : "lg:grid-cols-4"} gap-3`} data-testid="alert-cards">
      <AlertCard
        testid="alert-past-due"
        icon={WarningIcon} color="red"
        title="Vencidas" subtitle="Facturas vencidas"
        count={alerts.past_due.count} total={alerts.past_due.total}
        onClick={() => onNavigate("/cxc")}
      />
      <AlertCard
        testid="alert-por-vencer"
        icon={ClockIcon} color="yellow"
        title="Por vencer (7d)" subtitle="Próximas a vencer"
        count={alerts.por_vencer.count} total={alerts.por_vencer.total}
        onClick={() => onNavigate("/cxc")}
      />
      <AlertCard
        testid="alert-cobros-pendientes"
        icon={HandCoinsIcon} color="green"
        title="Cobros sin autorizar" subtitle="Pendientes del RTC"
        count={alerts.cobros_pendientes_auth.count} total={alerts.cobros_pendientes_auth.total}
        onClick={() => onNavigate("/cxc")}
      />
      <AlertCard
        testid="alert-inactivos"
        icon={UserCircleIcon} color="blue"
        title="Clientes inactivos" subtitle="Sin compra +60 días"
        count={alerts.clientes_inactivos.count}
        onClick={() => onNavigate("/clientes/dormidos")}
      />
      {hasCxp && (
        <AlertCard
          testid="alert-cxp"
          icon={ReceiptIcon} color="purple"
          title="CxP por pagar" subtitle={`${alerts.cxp_por_pagar.vencidas} vencidas · próximos 7 días`}
          count={alerts.cxp_por_pagar.count} total={alerts.cxp_por_pagar.total}
          onClick={() => onNavigate("/cxp")}
        />
      )}
    </div>
  );
}

function AlertCard({ icon: Icon, color, title, subtitle, count, total, onClick, testid }) {
  const palette = {
    red: { bg: "bg-red-500/10", border: "border-red-500/30", text: "text-red-400" },
    yellow: { bg: "bg-agro-yellow/10", border: "border-agro-yellow/30", text: "text-agro-yellow" },
    green: { bg: "bg-agro-green/10", border: "border-agro-green/30", text: "text-agro-green" },
    blue: { bg: "bg-blue-500/10", border: "border-blue-500/30", text: "text-blue-400" },
    purple: { bg: "bg-purple-500/10", border: "border-purple-500/30", text: "text-purple-400" },
  }[color];
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`text-left p-4 rounded-lg border ${palette.border} ${palette.bg} hover:scale-[1.01] transition`}
    >
      <div className="flex items-start justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-wider text-text-muted">{subtitle}</div>
          <div className={`font-heading text-base font-semibold mt-0.5 ${palette.text}`}>{title}</div>
        </div>
        <Icon size={20} weight="duotone" className={palette.text} />
      </div>
      <div className="mt-3 flex items-baseline gap-2">
        <span className={`font-heading text-3xl ${palette.text}`}>{count}</span>
        {total != null && <span className="text-xs text-text-secondary">· {fmtL(total)}</span>}
      </div>
      <div className="mt-2 text-[10px] text-text-muted flex items-center gap-1">
        Ver lista <ArrowRightIcon size={10} />
      </div>
    </button>
  );
}

function Pill({ color, n, l }) {
  const map = {
    red: "bg-red-500/15 text-red-400 border-red-500/30",
    yellow: "bg-agro-yellow/15 text-agro-yellow border-agro-yellow/30",
    green: "bg-agro-green/15 text-agro-green border-agro-green/30",
    blue: "bg-blue-500/15 text-blue-400 border-blue-500/30",
    orange: "bg-orange-500/15 text-orange-300 border-orange-500/30",
    purple: "bg-purple-500/15 text-purple-400 border-purple-500/30",
    teal: "bg-teal-500/15 text-teal-300 border-teal-500/30",
    fuchsia: "bg-fuchsia-500/15 text-fuchsia-300 border-fuchsia-500/40",
  };
  return (
    <div className={`text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded border ${map[color]} flex items-center justify-between`}>
      <span>{l}</span><span className="font-semibold">{n}</span>
    </div>
  );
}

function FilterChips({ filter, setFilter, ocultar = [] }) {
  const todos = [
    { k: "vencidas", l: "Vencidas", c: "red" },
    { k: "por_vencer", l: "Por vencer", c: "yellow" },
    { k: "cobros", l: "Cobros", c: "green" },
    { k: "promesas", l: "Promesas", c: "orange" },
    { k: "activacion", l: "Activación", c: "blue" },
    { k: "cxp", l: "CxP (pagos)", c: "purple" },
    { k: "prestamos", l: "Préstamos", c: "purple" },
    { k: "ausencias", l: "Ausencias RRHH", c: "gray" },
    { k: "actividades", l: "ACTIVIDADES PROGRAMADAS", c: "teal" },
    { k: "oportunidades", l: "OPORTUNIDADES SUGERIDAS", c: "fuchsia" },
  ];
  const items = todos.filter((it) => !ocultar.includes(it.k));
  const map = {
    red: "bg-red-500/10 text-red-400 border-red-500/30",
    yellow: "bg-agro-yellow/10 text-agro-yellow border-agro-yellow/30",
    green: "bg-agro-green/10 text-agro-green border-agro-green/30",
    blue: "bg-blue-500/10 text-blue-400 border-blue-500/30",
    purple: "bg-purple-500/10 text-purple-400 border-purple-500/30",
    orange: "bg-orange-500/10 text-orange-300 border-orange-500/30",
    gray: "bg-white/5 text-text-secondary border-border",
    teal: "bg-teal-500/10 text-teal-300 border-teal-500/30",
    fuchsia: "bg-fuchsia-500/10 text-fuchsia-300 border-fuchsia-500/40",
  };
  return (
    <div className="flex gap-1.5 flex-wrap">
      {items.map(it => (
        <button
          key={it.k}
          onClick={() => setFilter({ ...filter, [it.k]: !filter[it.k] })}
          data-testid={`filter-${it.k}`}
          className={`text-[10px] uppercase tracking-wider px-2 py-1 rounded border transition ${
            filter[it.k] ? map[it.c] : "border-border text-text-muted opacity-50"
          }`}
        >
          {it.l}
        </button>
      ))}
    </div>
  );
}

function EventRow({ ev, onClick, onPromesa, onMapa }) {
  const map = {
    vencida: { c: "text-red-400 border-l-red-500", i: WarningIcon, t: "VENCIDA" },
    por_vencer: { c: "text-agro-yellow border-l-agro-yellow", i: ClockIcon, t: "POR VENCER" },
    cobro_pendiente: { c: "text-agro-yellow border-l-agro-yellow", i: HandCoinsIcon, t: "COBRO PEND." },
    cobro_autorizado: { c: "text-agro-green border-l-agro-green", i: HandCoinsIcon, t: "COBRO" },
    cobro_rechazado: { c: "text-red-400 border-l-red-500", i: HandCoinsIcon, t: "COBRO RECH." },
    activacion: { c: "text-blue-400 border-l-blue-500", i: FlagIcon, t: "ACTIVACIÓN" },
    cxp: { c: "text-purple-400 border-l-purple-500", i: ReceiptIcon, t: "PAGO CxP" },
    promesa: { c: "text-orange-300 border-l-orange-400", i: HandCoinsIcon, t: "PROMESA DE PAGO" },
    promesa_vencida: { c: "text-red-400 border-l-red-500", i: HandCoinsIcon, t: "PROMESA VENCIDA" },
    prestamo: { c: "text-purple-400 border-l-purple-500", i: BankIcon, t: "CUOTA PRÉSTAMO" },
    ausencia: { c: "text-text-secondary border-l-border", i: AirplaneTiltIcon, t: "AUSENCIA RRHH" },
    visita: { c: "text-teal-300 border-l-teal-400", i: FlagIcon, t: "VISITA" },
    recordatorio: { c: "text-sky-300 border-l-sky-400", i: ClockIcon, t: "RECORDATORIO" },
    actividad: { c: "text-sky-300 border-l-sky-400", i: FlagIcon, t: "ACTIVIDAD" },
    contacto: { c: "text-teal-300 border-l-teal-400", i: FlagIcon, t: "CONTACTO COMERCIAL" },
  };
  const meta = map[ev.tipo] || { c: "text-text-secondary border-l-border", i: ReceiptIcon, t: ev.tipo };
  const Icon = meta.i;
  return (
    <button onClick={onClick} className={`w-full text-left border-b border-border/30 border-l-4 ${meta.c} p-3 hover:bg-white/[0.03] transition`}>
      <div className="flex items-start gap-2">
        <Icon size={14} weight="duotone" className={`mt-0.5 ${meta.c}`} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2">
            <span className={`text-[9px] uppercase tracking-wider font-bold ${meta.c}`}>{meta.t}</span>
            {ev.monto > 0 && <span className="text-xs font-medium text-text-primary">{fmtL(ev.monto)}</span>}
          </div>
          <div className="text-sm font-medium truncate">{ev.titulo}</div>
          {ev.cliente && <div className="text-[11px] text-text-secondary truncate">{ev.cliente}</div>}
          <div className="flex gap-2 text-[10px] text-text-muted mt-0.5">
            {ev.vendedor && <span>RTC: {ev.vendedor}</span>}
            {ev.region && <span>· {ev.region}</span>}
            {ev.ultima_compra && ev.ultima_compra !== "Nunca" && <span>· últ. compra: {fmtDate(ev.ultima_compra)}</span>}
            {ev.sin_fecha_vencimiento && <span>· sin fecha de vencimiento</span>}
          </div>
          {ev.motivo && <div className="text-[10px] text-agro-yellow/90 truncate" data-testid="ev-motivo">Motivo: {ev.motivo}</div>}
          {ev.nota && ev.nota !== ev.motivo && <div className="text-[10px] text-text-muted italic truncate">{ev.nota}</div>}
          {ev.tipo === "activacion" && ev.cliente_id && onMapa && (
            <span role="button" tabIndex={0} data-testid="ev-mapa-compras"
              onClick={(e) => { e.stopPropagation(); onMapa(ev); }}
              onKeyDown={(e) => { if (e.key === "Enter") { e.stopPropagation(); onMapa(ev); } }}
              className="inline-flex mt-1.5 text-[10px] px-2 py-0.5 rounded bg-blue-500/15 text-blue-300 hover:bg-blue-500/25 font-semibold uppercase tracking-wider">
              Qué ha comprado · qué ofrecer
            </span>
          )}
          {String(ev.link || "").startsWith("/ventas/promesas/") && (
            <span className="inline-flex mt-1.5 text-[10px] px-2 py-0.5 rounded bg-orange-500/15 text-orange-300 font-semibold uppercase tracking-wider" data-testid="ev-abrir-promesa">
              Abrir promesa · seguimiento
            </span>
          )}
          {ev.schedule_id && ev.estado === "pendiente" && onPromesa && !String(ev.link || "").startsWith("/ventas/promesas/") && (
            <div className="flex gap-2 mt-1">
              <span role="button" tabIndex={0} onClick={(e) => { e.stopPropagation(); onPromesa(ev, "cumplida"); }} className="text-[10px] px-2 py-0.5 rounded bg-agro-green/15 text-agro-green">Cumplida</span>
              <span role="button" tabIndex={0} onClick={(e) => { e.stopPropagation(); onPromesa(ev, "incumplida"); }} className="text-[10px] px-2 py-0.5 rounded bg-red-500/15 text-red-400">Incumplida</span>
            </div>
          )}
        </div>
      </div>
    </button>
  );
}

// ============== BLOQUE RTC HOY ==============
function RTCTodayBlock({ data, onNavigate }) {
  if (!data) return null;
  return (
    <div className="mt-6 bg-card border border-border/60 rounded-lg overflow-hidden" data-testid="rtc-today-block">
      <div className="p-4 border-b border-border/60 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <BellRingingIcon size={18} className="text-agro-green" weight="duotone" />
          <div>
            <div className="text-[10px] uppercase tracking-wider text-text-muted">Gestión del día</div>
            <h3 className="font-heading text-lg font-semibold">A gestionar HOY · {fmtDate(data.fecha)}</h3>
          </div>
        </div>
        <div className="flex gap-4 text-xs">
          <Stat l="Facturas" v={data.totales.facturas} />
          <Stat l="Vencidas" v={data.totales.vencidas} color="red" />
          <Stat l="Próximas" v={data.totales.proximas} color="yellow" />
          <Stat l="Monto" v={fmtL(data.totales.monto)} color="green" />
        </div>
      </div>

      {data.grupos.length === 0 ? (
        <div className="p-10 text-center text-sm text-text-muted">
          <ChartLineUpIcon size={32} className="mx-auto mb-2 opacity-40" />
          Sin facturas que requieran gestión hoy
        </div>
      ) : (
        <div className="divide-y divide-border/40">
          {data.grupos.map((g) => (
            <div key={g.rtc_id} className="p-4">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-full bg-agro-green/10 border border-agro-green/30 flex items-center justify-center">
                    <UserCircleIcon size={16} className="text-agro-green" weight="duotone" />
                  </div>
                  <div>
                    <div className="font-medium text-sm">{g.rtc_nombre}</div>
                    <div className="text-[10px] text-text-muted">{g.items.length} facturas · {fmtL(g.total)}</div>
                  </div>
                </div>
                <button
                  onClick={() => onNavigate("/cxc")}
                  className="text-[10px] uppercase tracking-wider text-agro-green hover:underline flex items-center gap-1"
                  data-testid={`rtc-go-${g.rtc_id}`}
                >
                  Ir a cobranza <ArrowRightIcon size={10} />
                </button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {g.items.slice(0, 6).map((it) => (
                  <div key={it.id} className={`p-2 rounded-md border text-xs ${it.vencida ? "border-red-500/30 bg-red-500/5" : "border-agro-yellow/30 bg-agro-yellow/5"}`}>
                    <div className="flex justify-between items-center">
                      <span className="font-mono text-agro-green">{it.numero}</span>
                      <span className="font-medium">{fmtL(it.saldo)}</span>
                    </div>
                    <div className="text-text-secondary truncate">{it.cliente}</div>
                    <div className="text-[10px] mt-0.5 flex justify-between">
                      <span className="text-text-muted">{it.region || "—"}</span>
                      <span className={it.vencida ? "text-red-400 font-medium" : "text-agro-yellow"}>
                        {it.vencida ? `${it.dias_vencidos}d vencida` : `vence ${fmtDate(it.fecha_vencimiento)}`}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
              {g.items.length > 6 && (
                <div className="text-[10px] text-text-muted mt-2">+{g.items.length - 6} más…</div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Stat({ l, v, color }) {
  const c = color === "red" ? "text-red-400" : color === "yellow" ? "text-agro-yellow" : color === "green" ? "text-agro-green" : "text-text-primary";
  return (
    <div className="text-right">
      <div className="text-[9px] uppercase tracking-wider text-text-muted">{l}</div>
      <div className={`font-heading text-base font-semibold ${c}`}>{v}</div>
    </div>
  );
}

// ============== VISTA SEMANAL ==============
// Agenda: los días del período con sus eventos, uno debajo del otro (vista del móvil)
function AgendaView({ events, pasa, today, loading, onOpen, onPromesa, onMapa }) {
  const dias = Object.keys(events).sort().map((k) => [k, (events[k] || []).filter(pasa)]).filter(([, evs]) => evs.length);
  if (loading) return <div className="p-8 text-center text-sm text-text-muted">Cargando…</div>;
  if (!dias.length) return <div className="p-8 text-center text-sm text-text-muted" data-testid="agenda-vacia">Nada agendado en este período.</div>;
  return (
    <div className="divide-y divide-border/60" data-testid="agenda-view">
      {dias.map(([k, evs]) => {
        const d = new Date(`${k}T12:00:00`);
        return (
          <section key={k} data-testid={`agenda-dia-${k}`}>
            <h4 className={`sticky top-0 z-10 bg-sidebar px-4 py-2 text-xs font-semibold flex items-center justify-between ${k === today ? "text-agro-green" : "text-text-secondary"}`}>
              <span className="capitalize">{d.toLocaleDateString("es-HN", { weekday: "long", day: "numeric", month: "long" })}{k === today ? " · hoy" : ""}</span>
              <span className="text-text-muted font-normal">{evs.length} {evs.length === 1 ? "evento" : "eventos"}</span>
            </h4>
            {evs.map((ev, i) => <EventRow key={i} ev={ev} onClick={() => onOpen(ev)} onPromesa={onPromesa} onMapa={onMapa} />)}
          </section>
        );
      })}
    </div>
  );
}

function WeekView({ weekStart, events, selected, today, onPick }) {
  const days = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));
  return (
    <div className="grid grid-cols-1 md:grid-cols-7 gap-px bg-border/40" data-testid="week-view">
      {days.map((d, i) => {
        const k = ymd(d);
        const evs = events[k] || [];
        const monto = evs.filter((e) => ["vencida", "por_vencer", "promesa", "promesa_vencida"].includes(e.tipo)).reduce((s, e) => s + (e.monto || 0), 0);
        return (
          <button key={k} onClick={() => onPick(k)} data-testid={`week-day-${k}`}
            className={`bg-card min-h-[220px] p-2 text-left align-top hover:bg-white/[0.03] ${k === selected ? "ring-2 ring-agro-green ring-inset" : ""}`}>
            <div className={`text-[10px] uppercase tracking-wider ${k === today ? "text-agro-green font-semibold" : "text-text-muted"}`}>{DIAS[i]} {d.getDate()}</div>
            {monto > 0 && <div className="text-[10px] text-agro-yellow mt-0.5">{fmtL(monto)} a cobrar</div>}
            <div className="mt-1 space-y-1">
              {evs.slice(0, 8).map((e, j) => (
                <div key={j} className={`text-[10px] truncate px-1 py-0.5 rounded border-l-2 bg-app/60 ${
                  e.tipo === "vencida" || e.tipo === "promesa_vencida" ? "border-red-500 text-red-300"
                  : e.tipo === "por_vencer" ? "border-agro-yellow text-agro-yellow"
                  : e.tipo === "promesa" ? "border-orange-400 text-orange-300"
                  : e.tipo.startsWith("cobro_") ? "border-agro-green text-agro-green"
                  : "border-border text-text-secondary"}`} title={`${e.titulo} · ${e.cliente || ""}`}>
                  {e.titulo}
                </div>
              ))}
              {evs.length > 8 && <div className="text-[10px] text-text-muted">+{evs.length - 8} más</div>}
            </div>
          </button>
        );
      })}
    </div>
  );
}

// ============== ALERTAS DE COBRANZA (HOY / SEMANA) ==============
function CobranzaStrip({ alerts, onPickDay }) {
  const c = alerts?.cobranza;
  if (!c) return null;
  return (
    <div className="mt-4 bg-card border border-border/60 rounded-lg p-4" data-testid="cobranza-strip">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
        <div>
          <div className="text-[10px] uppercase tracking-wider text-text-muted">Alertas de cobranza</div>
          <div className="text-sm">
            <span className="text-agro-yellow font-semibold">Hoy:</span> {c.hoy.facturas} facturas ({fmtL(c.hoy.monto_facturas)}) · {c.hoy.promesas} promesas ({fmtL(c.hoy.monto_promesas)})
            <span className="mx-2 text-text-muted">|</span>
            <span className="text-agro-green font-semibold">Semana:</span> {c.semana.facturas} facturas · {c.semana.promesas} promesas · {fmtL(c.semana.monto)}
          </div>
        </div>
        {c.promesas_vencidas.count > 0 && (
          <div className="text-xs text-red-400">{c.promesas_vencidas.count} promesas vencidas · {fmtL(c.promesas_vencidas.total)}</div>
        )}
      </div>
      <div className="grid grid-cols-7 gap-2">
        {c.semana.dias.map((d) => (
          <button key={d.fecha} onClick={() => onPickDay(d.fecha)} className="bg-app/60 border border-border/50 rounded p-2 text-left hover:border-agro-green/50">
            <div className="text-[10px] text-text-muted">{fmtDate(d.fecha)}</div>
            <div className="text-xs font-medium">{fmtL(d.monto_facturas + d.monto_promesas)}</div>
            <div className="text-[10px] text-text-muted">{d.facturas} fac · {d.promesas} prom</div>
          </button>
        ))}
      </div>
    </div>
  );
}

// ============== MODAL PROMESA / COBRO PROGRAMADO ==============
function PromesaModal({ initial, defaultDate, onClose, onSaved }) {
  const navigate = useNavigate();
  const [clientes, setClientes] = useState([]);
  const [facturas, setFacturas] = useState([]);
  const [form, setForm] = useState({
    cliente_id: initial.cliente_id || "", factura_id: initial.factura_id || "",
    fecha: initial.fecha || defaultDate, monto: initial.monto || "", nota: initial.nota || "", tipo: initial.tipo || "cobro_programado",
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    Promise.all([api.get("/clients").catch(() => ({ data: [] })), api.get("/receivables").catch(() => ({ data: [] }))])
      .then(([c, r]) => {
        const cl = Array.isArray(c.data) ? c.data : (c.data?.items || []);
        setClientes(cl);
        setFacturas(r.data || []);
      });
  }, []);
  const facturasCliente = facturas.filter((f) => f.cliente_id === form.cliente_id);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.cliente_id) { toast.error("Selecciona el cliente"); return; }
    setSaving(true);
    try {
      const payload = { ...form, factura_id: form.factura_id || null, monto: Number(form.monto) || 0 };
      if (initial.id) await api.put(`/calendar/collection-schedules/${initial.id}`, payload);
      else await api.post("/calendar/collection-schedules", payload);
      toast.success("Cobro programado guardado");
      onSaved();
    } catch (er) { toast.error(apiErr(er)); }
    finally { setSaving(false); }
  };

  const pm = useModalForm({ value: form, onClose: onClose, onSave: submit });
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" {...pm.overlayProps}>
      <div className="bg-card border border-border rounded-lg w-full max-w-md" onClick={(e) => e.stopPropagation()} data-testid="promesa-modal">
        <div className="p-4 border-b border-border font-heading font-semibold">Programar cobro, promesa, visita o recordatorio</div>
        <form onSubmit={pm.guardar} className="p-4 space-y-3 text-sm">
          <div>
            <label className="text-[10px] uppercase text-text-muted block mb-1">Tipo</label>
            <select value={form.tipo} onChange={(e) => (e.target.value === "promesa" ? navigate("/ventas/promesas/nueva") : setForm({ ...form, tipo: e.target.value }))} className="w-full bg-app border border-border rounded-md px-3 py-2">
              <option value="promesa">Promesa de pago (abre el formulario completo con adjuntos)</option>
              <option value="cobro_programado">Cobro programado (visita)</option>
              <option value="visita">Visita comercial / técnica</option>
              <option value="recordatorio">Recordatorio</option>
              <option value="actividad">Actividad de seguimiento</option>
            </select>
          </div>
          <div>
            <label className="text-[10px] uppercase text-text-muted block mb-1">Cliente *</label>
            <select required value={form.cliente_id} onChange={(e) => setForm({ ...form, cliente_id: e.target.value, factura_id: "" })} className="w-full bg-app border border-border rounded-md px-3 py-2" data-testid="promesa-cliente">
              <option value="">— Selecciona —</option>
              {clientes.map((c) => <option key={c.id} value={c.id}>{c.codigo ? `${c.codigo} · ` : ""}{c.nombre}</option>)}
            </select>
          </div>
          <div>
            <label className="text-[10px] uppercase text-text-muted block mb-1">Factura (opcional)</label>
            <select value={form.factura_id} onChange={(e) => {
              const f = facturasCliente.find((x) => x.id === e.target.value);
              setForm({ ...form, factura_id: e.target.value, monto: f ? f.saldo_pendiente : form.monto });
            }} className="w-full bg-app border border-border rounded-md px-3 py-2" data-testid="promesa-factura">
              <option value="">— Sin factura específica —</option>
              {facturasCliente.map((f) => <option key={f.id} value={f.id}>{f.numero} · saldo {fmtL(f.saldo_pendiente)}</option>)}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-[10px] uppercase text-text-muted block mb-1">Fecha *</label>
              <input type="date" required value={form.fecha} onChange={(e) => setForm({ ...form, fecha: e.target.value })} className="w-full bg-app border border-border rounded-md px-3 py-2" data-testid="promesa-fecha" />
            </div>
            <div>
              <label className="text-[10px] uppercase text-text-muted block mb-1">Monto (L.)</label>
              <input type="number" step="0.01" min="0" value={form.monto} onChange={(e) => setForm({ ...form, monto: e.target.value })} className="w-full bg-app border border-border rounded-md px-3 py-2" data-testid="promesa-monto" />
            </div>
          </div>
          <div>
            <label className="text-[10px] uppercase text-text-muted block mb-1">Nota</label>
            <textarea rows={2} value={form.nota} onChange={(e) => setForm({ ...form, nota: e.target.value })} className="w-full bg-app border border-border rounded-md px-3 py-2" />
          </div>
          <div className="flex justify-end gap-2 pt-2 border-t border-border/60">
            <button type="button" onClick={pm.cerrar} className="px-4 py-2 text-sm border border-border rounded-md text-text-secondary">Cancelar</button>
            <button type="submit" disabled={(saving) || pm.saving} className="px-5 py-2 text-sm bg-agro-green text-black font-medium rounded-md disabled:opacity-50" data-testid="promesa-save">{saving ? "Guardando…" : "Guardar"}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Sel({ l, v, on, ops, testid }) {
  return (
    <label className="flex flex-col gap-1 min-w-[180px]">
      <span className="text-[9px] tracking-wider text-text-muted">{l}</span>
      <select value={v} onChange={(e) => on(e.target.value)} data-testid={testid}
        className="bg-app border border-border rounded-md px-2 py-2 text-xs max-w-[260px]">
        <option value="">TODOS</option>
        {ops.map(([k, t]) => <option key={k} value={k}>{t}</option>)}
      </select>
    </label>
  );
}
